
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: 'https://public.readdy.ai/ai/img_res/f2a05a3f079cf5baec8cb34983d9977b.jpg',
      title: 'Professional Cleaning',
      subtitle: 'Made Simple'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20modern%20office%20space%20being%20professionally%20cleaned%2C%20sleek%20conference%20rooms%20with%20glass%20walls%2C%20contemporary%20workstations%2C%20cleaning%20staff%20working%20efficiently%2C%20bright%20natural%20lighting%2C%20organized%20workspace%2C%20clean%20carpets%20and%20surfaces%2C%20business%20environment%20with%20warm%20peach%20accents%20and%20spotless%20appearance&width=1920&height=1080&seq=hero-cleaning-2&orientation=landscape',
      title: 'Commercial Excellence',
      subtitle: 'Every Detail Matters'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Elegant%20apartment%20complex%20lobby%20being%20professionally%20cleaned%2C%20marble%20floors%20gleaming%2C%20contemporary%20furniture%20arranged%20perfectly%2C%20cleaning%20equipment%20visible%2C%20bright%20natural%20lighting%2C%20spotless%20common%20areas%2C%20upscale%20residential%20building%20interior%20with%20warm%20peach%20tones%20and%20pristine%20maintenance&width=1920&height=1080&seq=hero-cleaning-3&orientation=landscape',
      title: 'Residential Care',
      subtitle: 'Your Home Deserves the Best'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Modern%20fitness%20gym%20being%20professionally%20cleaned%20and%20sanitized%2C%20exercise%20equipment%20sparkling%20clean%2C%20bright%20workout%20area%20with%20mirrors%2C%20cleaning%20staff%20disinfecting%20surfaces%2C%20spotless%20locker%20rooms%2C%20professional%20gym%20maintenance%20with%20warm%20lighting%20and%20peach%20accent%20colors&width=1920&height=1080&seq=hero-cleaning-4&orientation=landscape',
      title: 'Specialized Services',
      subtitle: 'Gyms, Daycares & More'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Images */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ backgroundImage: `url(${slide.image})` }}
          />
        ))}
        {/* Peach Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-400/40 via-peach-300/30 to-orange-300/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-20">
        <div className="w-full max-w-4xl">
          <div className="p-8 md:p-12">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
              {slides[currentSlide].title}
              <span className="block text-orange-100 drop-shadow-2xl">{slides[currentSlide].subtitle}</span>
            </h1>
            <p className="text-xl text-white/95 mb-8 leading-relaxed max-w-2xl drop-shadow-xl">
              Transform your space with our premium cleaning services. We deliver spotless results 
              with eco-friendly products and meticulous attention to detail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" onClick={() => scrollToSection('quote')}>
                <i className="ri-calendar-check-line mr-2"></i>
                Book Now
              </Button>
              <Button variant="outline" size="lg" onClick={() => scrollToSection('services')}>
                <i className="ri-service-line mr-2"></i>
                Our Services
              </Button>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center space-x-3 bg-white/20 backdrop-blur-sm rounded-xl p-4 border-2 border-white/30">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                  <i className="ri-shield-check-line text-white text-xl drop-shadow-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-white drop-shadow-xl text-lg">Insured & Bonded</h3>
                  <p className="text-white/95 text-sm drop-shadow-lg font-medium">Complete protection</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/20 backdrop-blur-sm rounded-xl p-4 border-2 border-white/30">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                  <i className="ri-leaf-line text-white text-xl drop-shadow-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-white drop-shadow-xl text-lg">Eco-Friendly</h3>
                  <p className="text-white/95 text-sm drop-shadow-lg font-medium">Safe for family & pets</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/20 backdrop-blur-sm rounded-xl p-4 border-2 border-white/30">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                  <i className="ri-time-line text-white text-xl drop-shadow-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-white drop-shadow-xl text-lg">Flexible Scheduling</h3>
                  <p className="text-white/95 text-sm drop-shadow-lg font-medium">When you need us</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentSlide 
                  ? 'bg-white shadow-lg' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={() => goToSlide((currentSlide - 1 + slides.length) % slides.length)}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-xl"></i>
      </button>
      <button
        onClick={() => goToSlide((currentSlide + 1) % slides.length)}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 cursor-pointer"
      >
        <i className="ri-arrow-right-line text-xl"></i>
      </button>
    </section>
  );
}
